import {
  Box,
  Collapse,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ExpanderIcon } from "components";
import { useState } from "react";
import { QuantumDemandPersona } from "../../QuantumDemand.types";
import { RankBadge } from "../RankBadge";
import { SectionLabel } from "../SectionLabel";
import { SeeMoreButton } from "../SeeMoreButton";

interface PersonaRowProps {
  persona: QuantumDemandPersona;
  rank: number;
}

const PersonaRow = ({ persona, rank }: PersonaRowProps) => {
  const { isOpen, onToggle } = useDisclosure();

  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const topicColor = useColorModeValue("gray.600", "gray.300");
  const dividerColor = useColorModeValue("gray.100", "gray.600");

  const { jobLevel, jobFunction, topics } = persona;

  const headerCellProps = {
    px: 0,
    pt: 0,
    pb: "6px",
    borderColor: dividerColor,
    color: mutedColor,
    fontSize: "10px",
    fontWeight: "bold",
    letterSpacing: "0.05em",
  };

  const bodyCellProps = {
    px: 0,
    py: "7px",
    borderColor: dividerColor,
    color: topicColor,
    fontSize: "12px",
  };

  return (
    <Box>
      <Flex
        as="button"
        type="button"
        alignItems="center"
        gap="10px"
        w="100%"
        py={2.5}
        textAlign="left"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <RankBadge rank={rank} />
        <Text flex={1} fontSize="13px" fontWeight="semibold">
          {jobLevel} – {jobFunction}
        </Text>
        <Flex color={mutedColor}>
          <ExpanderIcon isOpen={isOpen} iconProps={{ w: 3.5, h: 3.5 }} />
        </Flex>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box pl="29px" pb="14px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th {...headerCellProps}>Topic</Th>
                <Th {...headerCellProps} isNumeric>
                  Score
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {topics.map(({ topic, score }, index) => {
                const borderBottom =
                  index === topics.length - 1 ? "none" : undefined;

                return (
                  <Tr key={topic}>
                    <Td {...bodyCellProps} borderBottom={borderBottom}>
                      {topic}
                    </Td>
                    <Td
                      {...bodyCellProps}
                      borderBottom={borderBottom}
                      isNumeric
                    >
                      {Math.round(score)}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
};

interface RankedPersonaListProps {
  title: string;
  personas: QuantumDemandPersona[];
  initialCount: number;
  emptyMessage: string;
}

export const RankedPersonaList = ({
  title,
  personas,
  initialCount,
  emptyMessage,
}: RankedPersonaListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const dividerColor = useColorModeValue("gray.100", "gray.600");

  const visiblePersonas = isExpanded
    ? personas
    : personas.slice(0, initialCount);

  return (
    <Box>
      <SectionLabel>{title}</SectionLabel>
      {personas.length === 0 ? (
        <Text fontSize="sm" color={mutedColor}>
          {emptyMessage}
        </Text>
      ) : (
        <>
          {visiblePersonas.map((persona, index) => (
            <Box
              key={`${persona.jobLevel}-${persona.jobFunction}`}
              borderTop={index === 0 ? undefined : "1px solid"}
              borderColor={dividerColor}
            >
              <PersonaRow persona={persona} rank={index + 1} />
            </Box>
          ))}
          {personas.length > initialCount && (
            <SeeMoreButton
              isExpanded={isExpanded}
              onToggle={() => setIsExpanded((expanded) => !expanded)}
            />
          )}
        </>
      )}
    </Box>
  );
};
